import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ChevronDown, ChevronUp, Copy, Film, Plus, RefreshCw, Save, Trash2,
} from "lucide-react";

import { useStore } from "../context/StoreContext";
import { PLATFORMS } from "../lib/platforms";
import { fmtDateLong, fmtTime } from "../lib/format";
import { PlatformIcon } from "../components/branding";
import { ExportMenu, GhostBtn, INPUT, PrimaryBtn, Spinner } from "../components/ui";

const FIELD = `${INPUT} text-[14px] leading-relaxed`;

const emptyScene = () => ({ shot: "", duration: "", visual: "", on_screen_text: "", voiceover: "", broll: "" });

function scenesToText(post, scenes) {
  const meta = PLATFORMS[post.platform] || { name: post.platform };
  const lines = [];
  lines.push(`${post.org || "Storyboard"} ${meta.name} storyboard`);
  lines.push(`${fmtDateLong(post.date)} at ${fmtTime(post.time)}`);
  if (post.title) lines.push(`Title: ${post.title}`);
  lines.push("");
  scenes.forEach((s, i) => {
    lines.push(`${i + 1}. ${s.shot || "Scene"}${s.duration ? `  (${s.duration})` : ""}`);
    if (s.visual) lines.push(`   Visual: ${s.visual}`);
    if (s.on_screen_text) lines.push(`   On screen text: ${s.on_screen_text}`);
    if (s.voiceover) lines.push(`   Voiceover: ${s.voiceover}`);
    if (s.broll) lines.push(`   B roll: ${s.broll}`);
    lines.push("");
  });
  return lines.join("\n").trim() + "\n";
}

function Labeled({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-faint mb-1">{label}</div>
      {children}
    </div>
  );
}

export default function StoryboardPage() {
  const { id } = useParams();
  const postId = Number(id);
  const navigate = useNavigate();
  const { posts, generateStoryboard, saveStoryboard, sbId, exportItem, showToast } = useStore();

  const post = useMemo(() => posts.find((p) => p.id === postId), [posts, postId]);
  const [scenes, setScenes] = useState(() => (post && post.storyboard ? post.storyboard : []));
  const [saving, setSaving] = useState(false);

  // Resync local scenes when the post's storyboard changes (after generate).
  useEffect(() => {
    setScenes(post && post.storyboard ? post.storyboard : []);
  }, [post && post.id, post && JSON.stringify(post && post.storyboard)]);

  const busy = sbId === postId;

  if (!post) {
    return (
      <div className="animate-fade-in max-w-lg mx-auto text-center py-20">
        <Film size={26} className="text-faint mx-auto mb-3" />
        <div className="text-foreground font-medium">That post could not be found</div>
        <p className="text-[13.5px] text-muted mt-1.5">It may have been deleted or regenerated.</p>
        <button onClick={() => navigate("/calendar")} className="mt-5 inline-flex items-center gap-1.5 text-sm text-foreground hover:opacity-80">
          <ArrowLeft size={15} /> Back to calendar
        </button>
      </div>
    );
  }

  const meta = PLATFORMS[post.platform] || { name: post.platform };
  const dirty = JSON.stringify(scenes) !== JSON.stringify(post.storyboard || []);

  const setScene = (i, key, val) => setScenes((arr) => arr.map((s, j) => (j === i ? { ...s, [key]: val } : s)));
  const move = (i, dir) => {
    setScenes((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = arr.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const remove = (i) => setScenes((arr) => arr.filter((_, j) => j !== i));
  const add = () => setScenes((arr) => [...arr, emptyScene()]);

  const save = async () => {
    setSaving(true);
    try {
      await saveStoryboard(postId, scenes);
    } catch {
      showToast("Could not save the storyboard");
    }
    setSaving(false);
  };

  const exportFile = () => {
    const blob = new Blob([scenesToText(post, scenes)], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(post.org || "remifo").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${post.platform}-storyboard.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = (fmt) => {
    if (fmt === "md") {
      exportFile();
      return;
    }
    exportItem(postId, "storyboard", fmt);
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(scenesToText(post, scenes));
      showToast("Script copied");
    } catch {
      showToast("Copy failed, select the text manually");
    }
  };

  const hasScenes = scenes.length > 0;

  return (
    <div className="animate-fade-in max-w-3xl">
      <button onClick={() => navigate("/calendar")} className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-foreground transition mb-4">
        <ArrowLeft size={15} /> Back to calendar
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <PlatformIcon id={post.platform} size={15} className="text-foreground" />
            <h1 className="text-[24px] sm:text-[28px] font-bold tracking-tight text-foreground" style={{ letterSpacing: "-0.02em" }}>
              Storyboard
            </h1>
          </div>
          <p className="text-muted text-[13.5px]">
            {meta.name} · {fmtDateLong(post.date)} · {fmtTime(post.time)}
          </p>
        </div>
        {hasScenes ? (
          <div className="flex items-center gap-2 flex-wrap">
            <GhostBtn onClick={() => generateStoryboard(postId)}>
              {busy ? <Spinner size={15} /> : <RefreshCw size={15} />}
              Regenerate
            </GhostBtn>
            <GhostBtn onClick={copyAll}>
              <Copy size={15} />
              Copy
            </GhostBtn>
            <ExportMenu
              options={[
                { key: "md", label: "Markdown (.md)" },
                { key: "pdf", label: "PDF" },
                { key: "docx", label: "Word (.docx)" },
              ]}
              onPick={handleExport}
            />
            <PrimaryBtn onClick={save} disabled={!dirty || saving}>
              <Save size={16} />
              Save
            </PrimaryBtn>
          </div>
        ) : null}
      </div>

      {post.body ? (
        <div className="bg-surface border border-border rounded-2xl p-4 mb-5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-faint mb-1.5">From this post</div>
          <p className="text-[13.5px] text-muted whitespace-pre-wrap leading-relaxed line-clamp-4">{post.body}</p>
        </div>
      ) : null}

      {!hasScenes ? (
        <div className="border border-dashed border-border rounded-2xl p-10 text-center bg-surface">
          <div className="w-12 h-12 rounded-2xl bg-foreground/[0.06] flex items-center justify-center mx-auto mb-4">
            <Film size={22} className="text-muted" />
          </div>
          <div className="text-foreground font-medium text-[15px]">No storyboard yet</div>
          <p className="text-[13.5px] text-muted mt-1.5 max-w-sm mx-auto">
            Generate a shot by shot storyboard and script from this post, grounded in your knowledge base.
          </p>
          <button
            onClick={() => generateStoryboard(postId)}
            disabled={busy}
            className="mt-5 inline-flex items-center gap-2 bg-accent text-accent-foreground rounded-xl px-5 py-2.5 text-sm font-semibold transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
          >
            {busy ? <Spinner size={16} /> : null}
            {busy ? "Generating" : "Generate storyboard"}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {scenes.map((s, i) => (
            <div key={i} className="bg-surface border border-border rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 rounded-lg bg-foreground/[0.06] text-foreground text-[12px] font-semibold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <input
                  className={`${INPUT} py-1.5 text-[14px] font-medium flex-1 min-w-0`}
                  placeholder="Shot label, e.g. Hook"
                  value={s.shot || ""}
                  onChange={(e) => setScene(i, "shot", e.target.value)}
                />
                <input
                  className={`${INPUT} py-1.5 text-[13px] !w-28 shrink-0`}
                  placeholder="3s"
                  value={s.duration || ""}
                  onChange={(e) => setScene(i, "duration", e.target.value)}
                />
                <div className="flex items-center shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="w-7 h-7 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition disabled:opacity-30">
                    <ChevronUp size={15} />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === scenes.length - 1} title="Move down" className="w-7 h-7 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition disabled:opacity-30">
                    <ChevronDown size={15} />
                  </button>
                  <button onClick={() => remove(i)} title="Remove scene" className="w-7 h-7 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <Labeled label="Visual">
                  <textarea className={`${FIELD} min-h-[72px]`} placeholder="What is on screen, framing and action" value={s.visual || ""} onChange={(e) => setScene(i, "visual", e.target.value)} />
                </Labeled>
                <Labeled label="On screen text">
                  <textarea className={`${FIELD} min-h-[72px]`} placeholder="Text overlay for this beat" value={s.on_screen_text || ""} onChange={(e) => setScene(i, "on_screen_text", e.target.value)} />
                </Labeled>
                <Labeled label="Voiceover">
                  <textarea className={`${FIELD} min-h-[72px]`} placeholder="Spoken line or dialogue" value={s.voiceover || ""} onChange={(e) => setScene(i, "voiceover", e.target.value)} />
                </Labeled>
                <Labeled label="B roll">
                  <textarea className={`${FIELD} min-h-[72px]`} placeholder="Supporting shot, optional" value={s.broll || ""} onChange={(e) => setScene(i, "broll", e.target.value)} />
                </Labeled>
              </div>
            </div>
          ))}

          <button
            onClick={add}
            className="w-full border border-dashed border-border rounded-2xl py-3 text-[13.5px] text-muted hover:text-foreground hover:border-foreground/30 transition flex items-center justify-center gap-1.5"
          >
            <Plus size={15} /> Add scene
          </button>
        </div>
      )}
    </div>
  );
}